import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import {
  analyzeAiAnswer,
  scoreCitability,
  checkCannibalization,
  checkClaims,
  scoreLead,
  mapQueriesToPages,
} from "@/lib/ai/jev-tools.functions";
import { logAiObservation } from "@/lib/ai-citations.functions";
import { AI_SURFACES, CORE_PROMPTS } from "@/content/ai-prompts";

export const Route = createFileRoute("/_authenticated/admin/ai-verktyg")({
  head: () => ({
    meta: [
      { title: "AI-verktyg — Fenny admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AiToolsPage,
});

const TABS = [
  { id: "svar", label: "Bedöm AI-svar" },
  { id: "citerbarhet", label: "Citerbarhet" },
  { id: "kannibal", label: "Kannibaliseringsvakt" },
  { id: "fakta", label: "Faktakoll" },
  { id: "lead", label: "Leadbedömning" },
  { id: "sokfragor", label: "Sökfrågor → sida" },
] as const;

const pct = (v: number | null | undefined) => (v == null ? "–" : `${Math.round(v * 100)}%`);
const input = "w-full rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm";
const btn = "rounded-full bg-mint px-4 py-2 text-sm font-medium text-black disabled:opacity-50";

function useRun<T, A>(fn: (a: { data: A }) => Promise<T>) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<T | null>(null);
  const run = async (data: A) => {
    setBusy(true);
    setErr(null);
    try {
      setRes(await fn({ data }));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, res, run };
}

function Status({ busy, err }: { busy: boolean; err: string | null }) {
  if (busy) return <span className="inline-flex items-center gap-2 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Bedömer…</span>;
  if (err) return <p role="alert" className="text-sm text-red-400">{err}</p>;
  return null;
}

function Meter({ label, value, bad }: { label: string; value: number | null | undefined; bad?: boolean }) {
  const v = value ?? 0;
  const warn = bad ? v >= 0.5 : false;
  return (
    <div className="rounded-lg border border-white/10 p-3">
      <div className="text-xs text-white/60">{label}</div>
      <div className={`text-lg font-semibold ${warn ? "text-red-400" : ""}`}>{pct(value)}</div>
    </div>
  );
}

function Card({ children, intro }: { children: ReactNode; intro: string }) {
  return (
    <section className="space-y-4">
      <p className="max-w-2xl text-sm text-white/60">{intro}</p>
      {children}
    </section>
  );
}

function AnswerTab() {
  const fn = useServerFn(analyzeAiAnswer);
  const saveFn = useServerFn(logAiObservation);
  const { busy, err, res, run } = useRun(fn);
  const [prompt, setPrompt] = useState(CORE_PROMPTS[0]);
  const [surface, setSurface] = useState<string>(AI_SURFACES[0].id);
  const [answer, setAnswer] = useState("");
  const [saved, setSaved] = useState(false);
  const save = async () => {
    if (!res) return;
    await saveFn({
      data: {
        source: surface,
        query: prompt,
        mentioned: res.mentioned >= 0.5,
        cited: res.cited >= 0.5,
        linked: res.linked >= 0.5,
        formulation: answer.slice(0, 2000),
        note: res.factError >= 0.5 ? "Jev: möjligt faktafel om Fenny" : null,
      },
    });
    setSaved(true);
  };
  return (
    <Card intro="Klistra in svaret från en AI-tjänst. Jev avgör om Fenny nämns, citeras, länkas, rekommenderas och om något påstående om Fenny är fel. Spara sedan som observation i AI-synlighet.">
      <div className="grid gap-3 md:grid-cols-2">
        <select className={input} value={prompt} onChange={(e) => setPrompt(e.target.value)}>
          {CORE_PROMPTS.map((p) => <option key={p}>{p}</option>)}
        </select>
        <select className={input} value={surface} onChange={(e) => setSurface(e.target.value)}>
          {AI_SURFACES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <textarea className={`${input} h-48`} placeholder="Klistra in AI-svaret här…" value={answer} onChange={(e) => { setAnswer(e.target.value); setSaved(false); }} />
      <button className={btn} disabled={busy || answer.length < 10} onClick={() => run({ prompt, answer })}>Bedöm svaret</button>
      <Status busy={busy} err={err} />
      {res && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Meter label="Nämns" value={res.mentioned} />
            <Meter label="Citeras som källa" value={res.cited} />
            <Meter label="Länkas" value={res.linked} />
            <Meter label="Rekommenderas" value={res.recommended} />
            <Meter label="Faktafel om Fenny" value={res.factError} bad />
            <Meter label="Ton (0 neg – 100 pos)" value={res.sentiment} />
            <Meter label="Konkurrenter nämns" value={res.competitors} />
          </div>
          <button className={btn} disabled={saved} onClick={save}>{saved ? "Sparad" : "Spara som observation"}</button>
        </>
      )}
    </Card>
  );
}

function CitabilityTab() {
  const fn = useServerFn(scoreCitability);
  const { busy, err, res, run } = useRun(fn);
  const [paths, setPaths] = useState("/\n/pulse\n/prime\n/priser\n/foto");
  const list = paths.split("\n").map((p) => p.trim()).filter((p) => p.startsWith("/")).slice(0, 10);
  return (
    <Card intro="Ange upp till tio adresser (en per rad). Jev läser den publicerade sidan och bedömer hur lätt den är för AI-tjänster att citera. Lägst poäng visas först — börja förbättra där.">
      <textarea className={`${input} h-36 font-mono`} value={paths} onChange={(e) => setPaths(e.target.value)} />
      <button className={btn} disabled={busy || !list.length} onClick={() => run({ paths: list })}>Bedöm sidorna</button>
      <Status busy={busy} err={err} />
      {res && (
        <table className="w-full text-sm">
          <thead className="text-left text-white/60"><tr><th>Sida</th><th>Totalt</th><th>Direkt svar</th><th>Fakta</th><th>Avsändare</th><th>Tunn</th></tr></thead>
          <tbody>
            {res.map((r) => (
              <tr key={r.path} className="border-t border-white/10">
                <td className="py-2">{r.path}</td>
                {r.error ? <td colSpan={5} className="text-red-400">{r.error}</td> : (
                  <>
                    <td className="font-semibold">{r.total}</td>
                    <td>{pct(r.directAnswer)}</td><td>{pct(r.concreteFacts)}</td><td>{pct(r.trust)}</td>
                    <td className={(r.thin ?? 0) >= 0.5 ? "text-red-400" : ""}>{pct(r.thin)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function CannibalTab() {
  const fn = useServerFn(checkCannibalization);
  const { busy, err, res, run } = useRun(fn);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  return (
    <Card intro="Innan ni skapar en ny sida: skriv rubrik och kort sammanfattning. Jev jämför med befintliga frågesidor och visar om någon redan svarar på samma sak.">
      <input className={input} placeholder="Rubrik för den nya sidan" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className={`${input} h-28`} placeholder="Kort sammanfattning" value={summary} onChange={(e) => setSummary(e.target.value)} />
      <button className={btn} disabled={busy || title.length < 3} onClick={() => run({ title, summary })}>Kontrollera</button>
      <Status busy={busy} err={err} />
      {res && (res.matches.length === 0 ? <p className="text-sm">Inga liknande sidor hittades.</p> : (
        <ul className="space-y-2 text-sm">
          {res.matches.map((m) => (
            <li key={m.id} className="flex justify-between gap-4 border-t border-white/10 pt-2">
              <a href={m.url} target="_blank" rel="noreferrer" className="underline">{m.title}</a>
              <span className={m.overlap >= 0.5 ? "font-semibold text-red-400" : "text-white/60"}>{pct(m.overlap)} överlapp</span>
            </li>
          ))}
        </ul>
      ))}
    </Card>
  );
}

function ClaimsTab() {
  const fn = useServerFn(checkClaims);
  const { busy, err, res, run } = useRun(fn);
  const [text, setText] = useState("");
  return (
    <Card intro="Klistra in en text innan publicering (blogg, case, sida). Jev flaggar bindande priser, fel leveranstid, fel kontorsorter och överdrivna påståenden.">
      <textarea className={`${input} h-48`} value={text} onChange={(e) => setText(e.target.value)} />
      <button className={btn} disabled={busy || text.length < 20} onClick={() => run({ text })}>Faktakolla</button>
      <Status busy={busy} err={err} />
      {res && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Meter label="Bindande pris" value={res.bindingPrice} bad />
          <Meter label="Fel leveranstid" value={res.wrongDelivery} bad />
          <Meter label="Fel kontorsorter" value={res.wrongCities} bad />
          <Meter label="Obelagda påståenden" value={res.unverifiable} bad />
        </div>
      )}
    </Card>
  );
}

const SERVICE_LABEL: Record<string, string> = { pulse: "Pulse", prime: "Prime", foto: "Foto", heritage: "Heritage", brollop: "Bröllop & familj", ovrigt: "Övrigt / oklart" };

function LeadTab() {
  const fn = useServerFn(scoreLead);
  const { busy, err, res, run } = useRun(fn);
  const [message, setMessage] = useState("");
  const [source, setSource] = useState("");
  return (
    <Card intro="Klistra in meddelandet från en förfrågan och gärna källan (t.ex. chatgpt). Jev avgör tjänst, hur varm förfrågan är, om det brådskar och om det är spam.">
      <input className={input} placeholder="Källa, t.ex. chatgpt eller google (valfritt)" value={source} onChange={(e) => setSource(e.target.value)} />
      <textarea className={`${input} h-36`} value={message} onChange={(e) => setMessage(e.target.value)} />
      <button className={btn} disabled={busy || message.length < 5} onClick={() => run({ message, source: source || undefined })}>Bedöm förfrågan</button>
      <Status busy={busy} err={err} />
      {res && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-lg border border-white/10 p-3"><div className="text-xs text-white/60">Tjänst</div><div className="text-lg font-semibold">{SERVICE_LABEL[res.service] ?? res.service}</div></div>
          <Meter label="Köpredo" value={res.warmth} />
          <Meter label="Vill starta inom en månad" value={res.urgent} />
          <Meter label="Spam" value={res.spam} bad />
        </div>
      )}
    </Card>
  );
}

function QueriesTab() {
  const fn = useServerFn(mapQueriesToPages);
  const { busy, err, res, run } = useRun(fn);
  const [text, setText] = useState("");
  const list = text.split("\n").map((q) => q.trim()).filter((q) => q.length > 1).slice(0, 15);
  return (
    <Card intro="Klistra in sökfrågor från Sökfrågor-vyn eller Search Console (en per rad, max 15). Jev väljer vilken sida som bör svara, eller visar att innehåll saknas.">
      <textarea className={`${input} h-36`} value={text} onChange={(e) => setText(e.target.value)} />
      <button className={btn} disabled={busy || !list.length} onClick={() => run({ queries: list })}>Matcha</button>
      <Status busy={busy} err={err} />
      {res && (
        <ul className="space-y-2 text-sm">
          {res.map((r) => (
            <li key={r.query} className="flex justify-between gap-4 border-t border-white/10 pt-2">
              <span>{r.query}</span>
              {r.match ? <a className="underline" href={r.match.url} target="_blank" rel="noreferrer">{r.match.title}</a> : <span className="text-amber-400">Innehåll saknas</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AiToolsPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("svar");
  return (
    <div className="space-y-6 p-4 md:p-8">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">AI-verktyg</h1>
        <p className="max-w-2xl text-sm text-white/60">Snabba AI-bedömningar för synlighet, citeringar, innehåll och leads. Siffrorna är sannolikheter — 50 % och uppåt räknas som ja.</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`rounded-full px-3 py-1 text-xs ${tab === t.id ? "bg-mint text-black" : "bg-white/5 text-white/70"}`}>{t.label}</button>
          ))}
        </div>
      </header>
      {tab === "svar" && <AnswerTab />}
      {tab === "citerbarhet" && <CitabilityTab />}
      {tab === "kannibal" && <CannibalTab />}
      {tab === "fakta" && <ClaimsTab />}
      {tab === "lead" && <LeadTab />}
      {tab === "sokfragor" && <QueriesTab />}
    </div>
  );
}
